import React, { useState } from 'react';
import {
	attachToDiscourse,
	deleteDiscourseAttachment,
	discardDiscourse,
	initializeNewDiscourse,
	prepareDefaultValues,
	prepareDiscourseItems,
	saveDiscourse,
	stashDiscourse,
	attachPrescription,
} from './discourse.service';
import useApiClient from '../../Base/Hooks/useApiClient';
import {
	DocumentItem,
	useLazyAttachmentDetailQuery,
	useLazyDiscourseQuery,
	useLazyDraftDiscourseQuery,
} from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import Loading from '../../../Components/Loading';
import usePatientMeta from '../../../Components/PatientProfile/Hooks/usePatientMeta';
import apiClient from '../../../Services/ApiClient';

export interface DiscourseValues {
	text?: string;
	diagnosis?: any;
	diagnoses?: any[];
	discourseId: string;
}

export interface DiscourseContextProps {
	activeDiscourseId?: string;
	setActiveDiscourseId: (newState: string) => void;
	isDiscourseSaving: boolean;
	setIsDiscourseSaving: (newState: boolean) => void;
	handleSaveDiscourse: () => void;
	removeAttachmentFromDiscourse: (attachmentId: string) => void;
	handleStashDiscourse: (newState: DiscourseValues) => void;
	handleDiscardActiveDiscourse: () => void;
	handleRefreshAttachments: () => void;
	draftDiscourseValues: DiscourseValues | undefined;
	isLoading: boolean;
	setDiscourseValues: (newState: DiscourseValues | undefined) => void;
	discourseValues?: DiscourseValues;
	discourseAttachments?: DocumentItem[];
	handleEditDiscourseRecord: (discourseId: string) => void;
	handleAttachToDiscourse: (attachmentId: string, type: string) => void;
	handleAddCustomAttachmentToDiscourse: ({
		attachmentId,
		type,
		attachmentTitle,
		sendToPatient,
	}: {
		attachmentId: string;
		type: string;
		attachmentTitle: string;
		sendToPatient: boolean;
	}) => void;
	handleAttachPrescription: (prescriptionId: string) => void;
}

export interface DiscourseContextProviderProps {
	children?: React.ReactChild | React.ReactChild[];
	patientId: string;
	workplaceId: string;
	loggedUserId: string;
}

export const DiscourseContext = React.createContext<DiscourseContextProps>({
	activeDiscourseId: undefined,
	isDiscourseSaving: false,
	draftDiscourseValues: { discourseId: '' },
	isLoading: false,
	discourseValues: undefined,
	discourseAttachments: undefined,
	setActiveDiscourseId: () => {},
	setIsDiscourseSaving: () => {},
	handleSaveDiscourse: () => {},
	removeAttachmentFromDiscourse: () => {},
	handleStashDiscourse: () => {},
	handleDiscardActiveDiscourse: () => {},
	handleRefreshAttachments: () => {},
	setDiscourseValues: () => {},
	handleEditDiscourseRecord: () => {},
	handleAttachToDiscourse: () => {},
	handleAddCustomAttachmentToDiscourse: () => {},
	handleAttachPrescription: () => {},
});

export const useDiscourseContext: () => DiscourseContextProps = () => {
	return React.useContext<DiscourseContextProps>(DiscourseContext);
};

export function DiscourseContextProvider({
	children,
	patientId,
	workplaceId,
	loggedUserId,
}: DiscourseContextProviderProps) {
	const { displayError } = useApiClient();
	const { reFetchMeta, patientDiagnosis, metaIsLoading } =
		usePatientMeta(patientId);
	const [getDraftDiscourseData] = useLazyDraftDiscourseQuery();
	const [getDiscourseData] = useLazyDiscourseQuery();
	const [getAttachmentData] = useLazyAttachmentDetailQuery();
	const [activeDiscourseId, setActiveDiscourseId] = useState<
		string | undefined
	>();
	const [isDiscourseSaving, setIsDiscourseSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [draftDiscourseValues, setDraftDiscourseValues] = useState<
		DiscourseValues | undefined
	>(undefined);
	const [discourseValues, setDiscourseValues] = useState<
		DiscourseValues | undefined
	>(undefined);
	const [updateTimer, setUpdateTimer] = useState<null | number>(null);
	const [retryTimer, setRetryTimer] = useState<null | number>(null);
	const [discourseAttachments, setDiscourseAttachments] = useState<
		DocumentItem[]
	>([]);
	const [editMode, setEditMode] = useState(false);

	useEffectAsync(async () => {
		if (!editMode) {
			await getDraftDiscourse();
		}
	}, [activeDiscourseId]);

	const stashDiscourseWithTimeout = (
		values: DiscourseValues,
		triggerByRetry: boolean = false,
	) => {
		updateTimer !== null && clearTimeout(updateTimer);
		if (triggerByRetry || !isDiscourseSaving) {
			// @ts-ignore
			setUpdateTimer(
				// @ts-ignore
				setTimeout(async () => {
					setIsDiscourseSaving(true);
					await handleStashDiscourse(values);
					setTimeout(async () => {
						setIsDiscourseSaving(false);
					}, 1500);
				}, 500),
			);
		} else {
			retryTimer !== null && clearTimeout(retryTimer);
			setRetryTimer(
				// @ts-ignore
				setTimeout(() => {
					stashDiscourseWithTimeout(values, true);
				}, 500),
			);
		}
	};

	useEffectAsync(async () => {
		if (discourseValues) {
			stashDiscourseWithTimeout(discourseValues);
		}
	}, [discourseValues]);

	const getDraftDiscourse = async () => {
		setIsLoading(true);
		const data = await getDraftDiscourseData({ patientId }).unwrap();
		if (data?.document) {
			setActiveDiscourseId(data?.document?.id);
			setDraftDiscourseValues(prepareDefaultValues(data?.document));
			setDiscourseAttachments(data?.document?.items as DocumentItem[]);
		} else {
			if (!activeDiscourseId) {
				await handleNewDiscourseInitialization();
			}
		}
		setIsLoading(false);
	};

	const handleNewDiscourseInitialization = async () => {
		if (!metaIsLoading) {
			const newDiscourse = await initializeNewDiscourse(
				workplaceId,
				patientId,
				loggedUserId,
				prepareDiscourseItems(
					{
						discourseId: '',
						diagnosis: patientDiagnosis?.mainDiagnosis,
						diagnoses: patientDiagnosis?.secondaryDiagnoses?.map(
							(item: any) => ({ secondaryDiagnosis: item }),
						),
					},
					true,
				),
			);
			setActiveDiscourseId(newDiscourse?.data?.id);
		}
	};

	const handleRefreshAttachments = async () => {
		if (editMode && activeDiscourseId) {
			await handleEditDiscourseRecord(activeDiscourseId);
		} else {
			await getDraftDiscourse();
		}
	};

	const handleSaveDiscourse = async () => {
		if (activeDiscourseId) {
			setIsLoading(true);
			try {
				const savedDiscourse = await saveDiscourse(activeDiscourseId);
				if (savedDiscourse) {
					if (editMode) {
						setEditMode(false);
					}
					setDiscourseAttachments([]);
					await reFetchMeta();
					await getDraftDiscourse();
					setActiveDiscourseId(undefined);
				}
			} catch (e: any) {
				displayError(e);
			}
			setIsLoading(false);
		}
	};

	const removeAttachmentFromDiscourse = async (attachmentId: string) => {
		if (activeDiscourseId) {
			await deleteDiscourseAttachment(activeDiscourseId, attachmentId);
		}
		await handleRefreshAttachments();
	};

	const handleStashDiscourse = async (values: DiscourseValues) => {
		if (!activeDiscourseId) {
			return;
		}
		if (values?.discourseId === activeDiscourseId) {
			const response = await stashDiscourse(
				activeDiscourseId,
				prepareDiscourseItems(values),
			);
		}
	};

	const handleDiscardActiveDiscourse = async () => {
		if (activeDiscourseId) {
			setIsLoading(true);
			try {
				const savedDiscourse = await discardDiscourse(activeDiscourseId);
				if (savedDiscourse) {
					setActiveDiscourseId(undefined);
					setDiscourseAttachments([]);
					await getDraftDiscourse();
				}
			} catch (e: any) {
				displayError(e);
			}
			setIsLoading(false);
		}
	};

	const handleEditDiscourseRecord = async (discourseId: string) => {
		setIsLoading(true);
		let id: string | undefined = undefined;

		if (editMode) {
			id = activeDiscourseId;
		} else {
			const newVersion = await apiClient.get(`/document/${discourseId}/copy`);
			if (newVersion.data.id) {
				id = newVersion.data.id;
			}
		}
		if (id) {
			const data = await getDiscourseData({
				id,
			}).unwrap();
			if (data?.document) {
				setEditMode(true);
				setActiveDiscourseId(data?.document?.id);
				setDraftDiscourseValues(prepareDefaultValues(data?.document));
				setDiscourseAttachments(data?.document?.items as DocumentItem[]);
			}
			setIsLoading(false);
		}
	};

	const handleAttachToDiscourse = async (
		attachmentId: string,
		type: string,
	) => {
		if (activeDiscourseId) {
			const attachmentData = await getAttachmentData({
				id: attachmentId,
			}).unwrap();
			if (attachmentData?.document?.items) {
				const attachmentTitle =
					attachmentData?.document?.items?.find(item => item?.type === 'title')
						?.extra?.text || null;
				const attachmentTemplate =
					attachmentData?.document?.items?.find(
						item => item?.type === 'pdfTemplateData',
					)?.extra?.text || null;
				try {
					await attachToDiscourse(
						activeDiscourseId,
						attachmentId,
						type,
						attachmentTitle,
						attachmentTemplate?.extra?.pdfData?.rawData?.sendToPatient,
					);
				} catch (e: any) {
					displayError(e);
				}
			}
		}
		await handleRefreshAttachments();
	};

	const handleAddCustomAttachmentToDiscourse = async ({
		attachmentId,
		type,
		attachmentTitle,
		sendToPatient,
	}: {
		attachmentId: string;
		type: string;
		attachmentTitle: string;
		sendToPatient: boolean;
	}) => {
		if (activeDiscourseId) {
			try {
				await attachToDiscourse(
					activeDiscourseId,
					attachmentId,
					type,
					attachmentTitle,
					sendToPatient,
				);
			} catch (e: any) {
				displayError(e);
			}
		}
		await handleRefreshAttachments();
	};

	/**
	 * Handles attaching prescription to dekurz
	 * @param prescriptionId
	 */
	const handleAttachPrescription = async (prescriptionId: string) => {
		if (activeDiscourseId && prescriptionId) {
			await attachPrescription(activeDiscourseId, prescriptionId);
		}

		await handleRefreshAttachments();
	};

	return (
		<DiscourseContext.Provider
			value={{
				activeDiscourseId,
				isDiscourseSaving,
				draftDiscourseValues,
				isLoading,
				discourseValues,
				discourseAttachments,
				setActiveDiscourseId,
				setIsDiscourseSaving,
				handleSaveDiscourse,
				removeAttachmentFromDiscourse,
				handleStashDiscourse,
				handleDiscardActiveDiscourse,
				handleRefreshAttachments,
				setDiscourseValues,
				handleEditDiscourseRecord,
				handleAttachToDiscourse,
				handleAddCustomAttachmentToDiscourse,
				handleAttachPrescription,
			}}>
			{(isLoading || metaIsLoading) && <Loading />}
			{!isLoading && !metaIsLoading && children}
		</DiscourseContext.Provider>
	);
}
